import type { DataGridColumn } from './data-grid.types';
import type {
  GridBackendAttributeFilter,
  GridBackendAttributeFilterOperator,
  GridBackendAttributeFilterType,
  BooleanColumnFilter,
  ColumnFilter,
  ColumnFilterMap,
  DateColumnFilter,
  NumericColumnFilter,
  SerializedColumnFilters,
  TextColumnFilter
} from './column-filter.types';

/**
 * Shared frozen empty set reused by callers that have no remappings to pass.
 * Lets `buildWhereFromFilters` / `buildAttributeFilters` accept a required
 * `remappedFields` parameter without forcing every test/legacy caller to
 * allocate a throwaway `new Set()`.
 */
export const EMPTY_REMAPPED_FIELDS: ReadonlySet<string> = new Set<string>();

// ── Serialisation ─────────────────────────────────────────────────────

/**
 * Converts a `ColumnFilterMap` to a JSON-serialisable array for IndexedDB persistence.
 *
 * @param filters - The active filter map.
 * @returns A serialisable array of `{ field, filter }` entries.
 */
export function serializeFilters(filters: ColumnFilterMap): SerializedColumnFilters {
  const entries: SerializedColumnFilters = new Array<{ field: string; filter: ColumnFilter }>();
  for (const [field, filter] of filters.entries()) {
    entries.push({ field, filter });
  }
  return entries;
}

/**
 * Rehydrates a `ColumnFilterMap` from the serialised IndexedDB representation.
 *
 * @param data - The serialised filter entries, or `undefined` when the field is absent.
 * @returns A `ColumnFilterMap` (may be empty).
 */
export function deserializeFilters(data: SerializedColumnFilters | undefined): ColumnFilterMap {
  const map: ColumnFilterMap = new Map<string, ColumnFilter>();
  if (data === undefined) {
    return map;
  }
  for (const entry of data) {
    map.set(entry.field, entry.filter);
  }
  return map;
}

// ── Active-filter check ───────────────────────────────────────────────

/**
 * Returns `true` when a filter would actually constrain results.
 * An inactive filter (e.g. empty text with "contains", or "all" for booleans) is ignored.
 *
 * @param filter - The filter to check.
 * @returns Whether the filter is meaningfully constraining.
 */
export function isFilterActive(filter: ColumnFilter): boolean {
  switch (filter.type) {
    case 'text':
      if (filter.operator === 'isEmpty' || filter.operator === 'isNotEmpty') {
        return true;
      }
      return filter.value.trim().length > 0;

    case 'numeric':
      if (filter.operator === 'isEmpty' || filter.operator === 'isNotEmpty') {
        return true;
      }
      if (filter.operator === 'between') {
        return filter.value !== null && filter.valueTo !== undefined && filter.valueTo !== null;
      }
      return filter.value !== null;

    case 'date':
      if (filter.operator === 'isEmpty' || filter.operator === 'isNotEmpty') {
        return true;
      }
      return filter.value !== null && filter.value.length > 0;

    case 'boolean':
      return filter.operator !== 'all';
  }
}

/** Chip shown in the group bar for one active column filter. */
export interface ActiveFilterChipEntry {
  /** Column field name. */
  readonly field: string;
  /** Column header label. */
  readonly header: string;
  /** Operator + value summary. */
  readonly summary: string;
}

/**
 * Builds a short display summary for an active column filter chip.
 *
 * @param filter - Active filter to summarise.
 * @param operatorLabel - Resolves a translated operator label.
 * @param booleanTrueLabel - Label for boolean `true`.
 * @param booleanFalseLabel - Label for boolean `false`.
 * @returns Chip summary text.
 */
export function summarizeColumnFilter(
  filter: ColumnFilter,
  operatorLabel: (operator: string) => string,
  booleanTrueLabel: string,
  booleanFalseLabel: string
): string {
  switch (filter.type) {
    case 'text':
      return `${operatorLabel(filter.operator)} "${filter.value}"`;
    case 'numeric':
      if (
        filter.operator === 'between' &&
        filter.value !== null &&
        filter.valueTo !== undefined &&
        filter.valueTo !== null
      ) {
        return `${filter.value} – ${filter.valueTo}`;
      }
      return filter.value !== null
        ? `${operatorLabel(filter.operator)} ${filter.value}`
        : operatorLabel(filter.operator);
    case 'date':
      return filter.value !== null
        ? `${operatorLabel(filter.operator)} ${filter.value}`
        : operatorLabel(filter.operator);
    case 'boolean':
      if (filter.operator === 'true') {
        return booleanTrueLabel;
      }
      if (filter.operator === 'false') {
        return booleanFalseLabel;
      }
      return filter.operator;
  }
}

/**
 * Builds removable filter-chip entries for active filters.
 *
 * @param filters - Current column filter map.
 * @param headerMap - Field → header labels.
 * @param operatorLabel - Resolves a translated operator label.
 * @param booleanTrueLabel - Label for boolean `true`.
 * @param booleanFalseLabel - Label for boolean `false`.
 * @returns Chip entries in map iteration order.
 */
export function buildActiveFilterChipEntries(
  filters: ColumnFilterMap,
  headerMap: Map<string, string>,
  operatorLabel: (operator: string) => string,
  booleanTrueLabel: string,
  booleanFalseLabel: string
): Array<ActiveFilterChipEntry> {
  const entries: Array<ActiveFilterChipEntry> = new Array<ActiveFilterChipEntry>();
  if (filters.size === 0) {
    return entries;
  }

  for (const [field, filter] of filters.entries()) {
    if (!isFilterActive(filter)) {
      continue;
    }
    entries.push({
      field,
      header: headerMap.get(field) ?? field,
      summary: summarizeColumnFilter(filter, operatorLabel, booleanTrueLabel, booleanFalseLabel)
    });
  }
  return entries;
}

// ── Client-side row filtering (Storybook / flat presentational use) ───

/**
 * Returns rows that satisfy every active column filter on shared columns.
 * Used by Storybook and other client-side hosts; server orchestration filters via GraphQL instead.
 *
 * @param rows - Source rows to filter.
 * @param columns - Column definitions (only shared-column filters are applied).
 * @param filters - Active column filter map.
 */
export function filterRowsByColumnFilters(
  rows: Array<Record<string, unknown>>,
  columns: Array<DataGridColumn>,
  filters: ColumnFilterMap
): Array<Record<string, unknown>> {
  const sharedFieldNames: Set<string> = new Set<string>(
    columns.map((col: DataGridColumn): string => col.field)
  );
  const activeFilters: Array<[string, ColumnFilter]> = Array.from(filters.entries()).filter(
    ([field, filter]: [string, ColumnFilter]): boolean =>
      sharedFieldNames.has(field) && isFilterActive(filter)
  );

  if (activeFilters.length === 0) {
    return rows;
  }

  return rows.filter((row: Record<string, unknown>): boolean =>
    activeFilters.every(([field, filter]: [string, ColumnFilter]): boolean =>
      rowMatchesColumnFilter(row, field, filter)
    )
  );
}

/**
 * Returns whether a single row value satisfies a column filter.
 *
 * @param row - The data row.
 * @param field - The column field name.
 * @param filter - The filter to test against.
 */
function rowMatchesColumnFilter(row: Record<string, unknown>, field: string, filter: ColumnFilter): boolean {
  const rawValue: unknown = row[field];

  switch (filter.type) {
    case 'text':
      return matchesTextFilterValue(String(rawValue ?? ''), filter);
    case 'numeric':
      return matchesNumericFilterValue(rawValue, filter);
    case 'date':
      return matchesDateFilterValue(rawValue, filter);
    case 'boolean':
      return matchesBooleanFilterValue(rawValue, filter);
  }
}

function matchesTextFilterValue(value: string, filter: TextColumnFilter): boolean {
  const search: string = filter.value;

  switch (filter.operator) {
    case 'contains':
      return value.includes(search);
    case 'ncontains':
      return !value.includes(search);
    case 'equals':
      return value === search;
    case 'nequals':
      return value !== search;
    case 'startsWith':
      return value.startsWith(search);
    case 'nstartsWith':
      return !value.startsWith(search);
    case 'endsWith':
      return value.endsWith(search);
    case 'nendsWith':
      return !value.endsWith(search);
    case 'isEmpty':
      return value.length === 0;
    case 'isNotEmpty':
      return value.length > 0;
    default:
      return true;
  }
}

function matchesNumericFilterValue(value: unknown, filter: NumericColumnFilter): boolean {
  if (filter.operator === 'isEmpty') {
    return value === null || value === undefined || value === '';
  }
  if (filter.operator === 'isNotEmpty') {
    return value !== null && value !== undefined && value !== '';
  }

  const numeric: number | null = typeof value === 'number' ? value : Number(value);
  if (numeric === null || Number.isNaN(numeric)) {
    return false;
  }

  if (filter.operator === 'between') {
    if (filter.value === null || filter.valueTo === undefined || filter.valueTo === null) {
      return true;
    }
    return numeric >= filter.value && numeric <= filter.valueTo;
  }

  if (filter.value === null) {
    return true;
  }

  switch (filter.operator) {
    case 'eq':
      return numeric === filter.value;
    case 'neq':
      return numeric !== filter.value;
    case 'gt':
      return numeric > filter.value;
    case 'gte':
      return numeric >= filter.value;
    case 'lt':
      return numeric < filter.value;
    case 'lte':
      return numeric <= filter.value;
    default:
      return true;
  }
}

function matchesDateFilterValue(value: unknown, filter: DateColumnFilter): boolean {
  if (filter.operator === 'isEmpty') {
    return value === null || value === undefined || value === '';
  }
  if (filter.operator === 'isNotEmpty') {
    return value !== null && value !== undefined && value !== '';
  }

  const rowDate: string | null = value === null || value === undefined ? null : String(value);
  const filterDate: string | null = filter.value;

  if (rowDate === null || filterDate === null || filterDate.length === 0) {
    return false;
  }

  const rowTime: number = Date.parse(rowDate.length === 10 ? `${rowDate}T00:00:00.000Z` : rowDate);
  const filterTime: number = Date.parse(
    filterDate.length === 10 ? `${filterDate}T00:00:00.000Z` : filterDate
  );

  if (Number.isNaN(rowTime) || Number.isNaN(filterTime)) {
    return false;
  }

  switch (filter.operator) {
    case 'eq':
      return rowTime === filterTime;
    case 'before':
      return rowTime < filterTime;
    case 'after':
      return rowTime > filterTime;
    default:
      return true;
  }
}

function matchesBooleanFilterValue(value: unknown, filter: BooleanColumnFilter): boolean {
  if (filter.operator === 'all') {
    return true;
  }

  const boolValue: boolean = value === true || value === 'true';
  return filter.operator === 'true' ? boolValue : !boolValue;
}

// ── HotChocolate `where` clause builder (shared columns) ─────────────

/**
 * Builds additional `where` clause entries from column filters, scoped to
 * shared (top-level) element fields only.
 *
 * The returned object should be merged into the existing `where` via spread:
 * ```ts
 * const where = { ...baseWhere, ...buildWhereFromFilters(filters, sharedColumns) };
 * ```
 *
 * @param filters - The current column filter map.
 * @param sharedColumns - The shared column definitions (used to determine which fields are top-level).
 * @param remappedFields - Canonical remapped field names. These appear in `sharedColumns` for
 *   rendering but are JSONB attributes for querying, so they are excluded here and handled by
 *   `buildAttributeFilters()` instead. Pass `EMPTY_REMAPPED_FIELDS` when no remappings exist.
 * @returns A partial HotChocolate `ElementFilterInput` object.
 */
export function buildWhereFromFilters(
  filters: ColumnFilterMap,
  sharedColumns: Array<DataGridColumn>,
  remappedFields: ReadonlySet<string>
): Record<string, unknown> {
  const where: Record<string, unknown> = {};
  const sharedFieldNames: Set<string> = new Set<string>(
    sharedColumns.map((col: DataGridColumn): string => col.field)
  );

  for (const [field, filter] of filters.entries()) {
    if (!sharedFieldNames.has(field)) {
      continue;
    }
    // Remapped canonical columns are JSONB attributes, not top-level fields.
    if (remappedFields.has(field)) {
      continue;
    }
    if (!isFilterActive(filter)) {
      continue;
    }

    const clause: Record<string, unknown> | null = mapFilterToHotChocolateClause(filter);
    if (clause !== null) {
      where[field] = clause;
    }
  }

  return where;
}

/**
 * Maps a single `ColumnFilter` to a HotChocolate filter clause object.
 *
 * @param filter - The column filter.
 * @returns A HotChocolate-style filter clause, or `null` if the filter is not translatable.
 */
function mapFilterToHotChocolateClause(filter: ColumnFilter): Record<string, unknown> | null {
  switch (filter.type) {
    case 'text':
      return mapTextFilterClause(filter.operator, filter.value);

    case 'numeric':
      if (filter.operator === 'between') {
        if (filter.value !== null && filter.valueTo !== undefined && filter.valueTo !== null) {
          return { gte: filter.value, lte: filter.valueTo };
        }
        return null;
      }
      return mapComparableFilterClause(filter.operator, filter.value);

    case 'date':
      return mapComparableFilterClause(
        mapDateOperatorToComparable(filter.operator),
        toIsoDateTime(filter.value)
      );

    case 'boolean':
      if (filter.operator === 'true') {
        return { eq: true };
      }
      if (filter.operator === 'false') {
        return { eq: false };
      }
      return null;
  }
}

/**
 * Maps a text filter operator + value to a HotChocolate string filter clause.
 *
 * @param operator - The text operator.
 * @param value - The search string.
 * @returns A HotChocolate clause object.
 */
function mapTextFilterClause(operator: string, value: string): Record<string, unknown> | null {
  switch (operator) {
    case 'contains':
      return { contains: value };
    case 'ncontains':
      return { ncontains: value };
    case 'equals':
      return { eq: value };
    case 'nequals':
      return { neq: value };
    case 'startsWith':
      return { startsWith: value };
    case 'nstartsWith':
      return { nstartsWith: value };
    case 'endsWith':
      return { endsWith: value };
    case 'nendsWith':
      return { nendsWith: value };
    case 'isEmpty':
      return { eq: null };
    case 'isNotEmpty':
      return { neq: null };
    default:
      return null;
  }
}

/**
 * Maps a comparable filter operator (numeric/date) + value to a HotChocolate clause.
 *
 * @param operator - The comparable operator.
 * @param value - The comparison value.
 * @returns A HotChocolate clause object.
 */
function mapComparableFilterClause(operator: string, value: unknown): Record<string, unknown> | null {
  switch (operator) {
    case 'eq':
      return { eq: value };
    case 'neq':
      return { neq: value };
    case 'gt':
      return { gt: value };
    case 'gte':
      return { gte: value };
    case 'lt':
      return { lt: value };
    case 'lte':
      return { lte: value };
    case 'isEmpty':
      return { eq: null };
    case 'isNotEmpty':
      return { neq: null };
    default:
      return null;
  }
}

/**
 * Maps a date filter operator to the equivalent comparable operator.
 *
 * @param operator - The date-specific operator.
 * @returns The equivalent comparable operator string.
 */
function mapDateOperatorToComparable(operator: string): string {
  switch (operator) {
    case 'before':
      return 'lt';
    case 'after':
      return 'gt';
    default:
      return operator;
  }
}

/**
 * Converts a date string (e.g. `2026-05-26` from the HTML date input) to a full
 * ISO 8601 DateTime string (`2026-05-26T00:00:00.000Z`) required by HotChocolate's
 * `DateTime` scalar type.
 *
 * @param value - The date input value (`YYYY-MM-DD`), or `null`.
 * @returns A full ISO DateTime string, or `null` if the input is null/empty.
 */
function toIsoDateTime(value: string | null): string | null {
  if (value === null || value.length === 0) {
    return null;
  }
  // If already contains 'T', assume it's already a full DateTime string
  if (value.includes('T')) {
    return value;
  }
  return `${value}T00:00:00.000Z`;
}

// ── Attribute filter builder (JSONB columns) ─────────────────────────

/**
 * Builds the `attributeFilter` GraphQL parameter from column filters,
 * scoped to type-specific (JSONB `custom_props`) columns only.
 *
 * @param filters - The current column filter map.
 * @param sharedColumns - Shared column definitions (used to exclude them).
 * @param discriminatorColumns - Type-specific column definitions keyed by element type.
 * @param remappedFields - Canonical remapped field names. These live in `sharedColumns` for
 *   rendering but are JSONB attributes for querying, so they are emitted as attribute filters
 *   (the backend resolves the per-entity key from its server-side alias catalog). Pass
 *   `EMPTY_REMAPPED_FIELDS` when no remappings exist.
 * @returns An array of `GridBackendAttributeFilter` objects for the GraphQL query.
 */
export function buildAttributeFilters(
  filters: ColumnFilterMap,
  sharedColumns: Array<DataGridColumn>,
  discriminatorColumns: Record<string, Array<DataGridColumn>>,
  remappedFields: ReadonlySet<string>
): Array<GridBackendAttributeFilter> {
  const sharedFieldNames: Set<string> = new Set<string>(
    sharedColumns.map((col: DataGridColumn): string => col.field)
  );

  // Lookup for remapped canonical column definitions (which live in `sharedColumns`).
  // Only built when remappings are actually configured — for tenants without
  // remappings the map would be built and immediately thrown away on every call,
  // and `buildAttributeFilters` is invoked up to MAX_PARALLEL_GROUPS (50) times
  // per grouped refresh.
  const sharedColumnMap: Map<string, DataGridColumn> | null =
    remappedFields.size > 0
      ? new Map<string, DataGridColumn>(
          sharedColumns.map((col: DataGridColumn): [string, DataGridColumn] => [col.field, col])
        )
      : null;

  const allTypeSpecificColumns: Map<string, DataGridColumn> = new Map<string, DataGridColumn>();
  for (const columns of Object.values(discriminatorColumns)) {
    for (const col of columns) {
      if (!allTypeSpecificColumns.has(col.field)) {
        allTypeSpecificColumns.set(col.field, col);
      }
    }
  }

  const entries: Array<GridBackendAttributeFilter> = new Array<GridBackendAttributeFilter>();

  for (const [field, filter] of filters.entries()) {
    const isRemapped: boolean = remappedFields.has(field);
    // True shared (top-level) columns are handled by buildWhereFromFilters().
    // Remapped canonical columns are also in sharedColumns but are attributes.
    if (sharedFieldNames.has(field) && !isRemapped) {
      continue;
    }
    if (!isFilterActive(filter)) {
      continue;
    }

    // `isRemapped === true` implies `remappedFields.size > 0`, which is exactly
    // when `sharedColumnMap` is non-null — so the lookup is well-defined.
    const column: DataGridColumn | undefined = isRemapped
      ? (sharedColumnMap as Map<string, DataGridColumn>).get(field)
      : allTypeSpecificColumns.get(field);
    if (column === undefined) {
      continue;
    }

    const entry: GridBackendAttributeFilter | null = mapFilterToAttributeEntry(field, filter, column);
    if (entry !== null) {
      entries.push(entry);

      // For 'between' filters, also emit the upper-bound (LTE) entry.
      if (
        filter.type === 'numeric' &&
        filter.operator === 'between' &&
        filter.valueTo !== undefined &&
        filter.valueTo !== null
      ) {
        const typeHint: GridBackendAttributeFilterType | undefined = resolveTypeHint(column);
        entries.push({
          name: field,
          operator: 'LTE',
          value: String(filter.valueTo),
          type: typeHint ?? 'DECIMAL'
        });
      }
    }
  }

  return entries;
}

/**
 * Maps a single `ColumnFilter` + column definition to an `GridBackendAttributeFilter`.
 *
 * @param field - The JSONB key name.
 * @param filter - The column filter.
 * @param column - The column definition (for type hint resolution).
 * @returns An `GridBackendAttributeFilter`, or `null` if the filter is not translatable.
 */
function mapFilterToAttributeEntry(
  field: string,
  filter: ColumnFilter,
  column: DataGridColumn
): GridBackendAttributeFilter | null {
  const typeHint: GridBackendAttributeFilterType | undefined = resolveTypeHint(column);

  switch (filter.type) {
    case 'text': {
      const operator: GridBackendAttributeFilterOperator | null = mapTextOperatorToBackend(filter.operator);
      if (operator === null) {
        return null;
      }
      // For isEmpty/isNotEmpty, send an empty string — backend handles null semantics
      const value: string =
        filter.operator === 'isEmpty' || filter.operator === 'isNotEmpty' ? '' : filter.value;
      return { name: field, operator, value, type: typeHint };
    }

    case 'numeric': {
      if (filter.operator === 'between') {
        if (filter.value === null || filter.valueTo === undefined || filter.valueTo === null) {
          return null;
        }
        // Range filters emit two attribute entries — the caller collects them into the array.
        // We return the GTE entry here; the LTE entry is appended in buildAttributeFilters.
        return {
          name: field,
          operator: 'GTE',
          value: String(filter.value),
          type: typeHint ?? 'DECIMAL'
        };
      }
      const operator: GridBackendAttributeFilterOperator | null = mapNumericOperatorToBackend(
        filter.operator
      );
      if (operator === null || filter.value === null) {
        return null;
      }
      return {
        name: field,
        operator,
        value: String(filter.value),
        type: typeHint ?? 'DECIMAL'
      };
    }

    case 'date': {
      const operator: GridBackendAttributeFilterOperator | null = mapDateOperatorToBackend(filter.operator);
      if (operator === null || filter.value === null) {
        return null;
      }
      return {
        name: field,
        operator,
        value: filter.value,
        type: typeHint ?? 'DATE'
      };
    }

    case 'boolean': {
      if (filter.operator === 'all') {
        return null;
      }
      return {
        name: field,
        operator: 'EQ',
        value: filter.operator,
        type: typeHint ?? 'TEXT'
      };
    }
  }
}

/**
 * Resolves the backend `SortCastType` hint from a column definition.
 *
 * @param column - The column definition.
 * @returns The type hint string, or `undefined` for text (the backend default).
 */
function resolveTypeHint(column: DataGridColumn): GridBackendAttributeFilterType | undefined {
  switch (column.type) {
    case 'numeric':
      return 'DECIMAL';
    case 'date':
      return 'DATE';
    default:
      return undefined;
  }
}

/**
 * Maps a frontend text filter operator to the backend enum value.
 *
 * @param operator - The frontend operator.
 * @returns The backend operator, or `null` for unsupported operators.
 */
function mapTextOperatorToBackend(operator: string): GridBackendAttributeFilterOperator | null {
  switch (operator) {
    case 'contains':
      return 'CONTAINS';
    case 'ncontains':
      return 'NOT_CONTAINS';
    case 'equals':
      return 'EQ';
    case 'nequals':
      return 'NEQ';
    case 'startsWith':
      return 'STARTS_WITH';
    case 'nstartsWith':
      return 'NOT_STARTS_WITH';
    case 'endsWith':
      return 'ENDS_WITH';
    case 'nendsWith':
      return 'NOT_ENDS_WITH';
    default:
      return null;
  }
}

/**
 * Maps a frontend numeric filter operator to the backend enum value.
 *
 * @param operator - The frontend operator.
 * @returns The backend operator, or `null` for unsupported operators.
 */
function mapNumericOperatorToBackend(operator: string): GridBackendAttributeFilterOperator | null {
  switch (operator) {
    case 'eq':
      return 'EQ';
    case 'neq':
      return 'NEQ';
    case 'gt':
      return 'GT';
    case 'gte':
      return 'GTE';
    case 'lt':
      return 'LT';
    case 'lte':
      return 'LTE';
    default:
      return null;
  }
}

/**
 * Maps a frontend date filter operator to the backend enum value.
 *
 * @param operator - The frontend operator.
 * @returns The backend operator, or `null` for unsupported operators.
 */
function mapDateOperatorToBackend(operator: string): GridBackendAttributeFilterOperator | null {
  switch (operator) {
    case 'eq':
      return 'EQ';
    case 'before':
      return 'LT';
    case 'after':
      return 'GT';
    default:
      return null;
  }
}
